import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import { useLocation, NavLink } from "react-router-dom";
import { showHeaderUi } from "../../helpers/general";
import { device, useBreakpoints } from "../../lib/breakpoints";
import Button from "../Button";
import { AuthContext } from "../../context/AuthProvider";
import { Link } from "react-router-dom";
import Hidden from "../Hidden";
import {
  DIALOG_NAME,
  useDialogSearchParam,
  useOpenParamDialog,
} from "../../helpers/useDialogSearchParam";
import FeedDrawer from "../feed/FeedDrawer";
import { firebaseAuth } from "../../helpers/authentication";
import { Dropdown } from "antd";
import { EditModal, SettingsModal } from "../profile";
import SolvTaxLogo from "./Logo";
import { Menu, X } from "lucide-react";
import { trackPortalCta } from "../../lib/siteActivity";

const profileIcon = "/icons/profile.svg";

const NAV_ITEMS = [
  { to: "/#how-it-works", label: "How It Works", hash: true },
  { to: "/pricing", label: "Pricing" },
  { to: "/help", label: "Help" },
  { to: "/contact", label: "Contact" },
];

export default function NavBar() {
  const location = useLocation();
  const openLogin = useOpenParamDialog(DIALOG_NAME.login);
  const openWaitlist = useOpenParamDialog(DIALOG_NAME.waitlist);
  const { user, userInfo } = useContext(AuthContext);
  const { isMobile } = useBreakpoints();
  const [feedDrawerVisible, setFeedDrawerVisible] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [isEditParamSet, removeEditParam] = useDialogSearchParam(DIALOG_NAME.edit);
  const [isSettingsParamSet, removeSettingsParam] = useDialogSearchParam(DIALOG_NAME.settings);

  const isHomePage = location.pathname === "/";

  const handleOpenLogin = (event) => {
    event.preventDefault();
    setMobileMenuOpen(false);
    openLogin();
  };

  const handleOpenWaitlist = (event) => {
    event.preventDefault();
    setMobileMenuOpen(false);
    trackPortalCta(isMobile ? "nav-mobile" : "nav-desktop");
    openWaitlist();
  };

  const handleHowItWorksClick = (event) => {
    setMobileMenuOpen(false);
    if (location.pathname === "/") {
      event.preventDefault();
      document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" });
      window.history.replaceState(null, "", "/#how-it-works");
    }
  };

  const onLogout = async () => {
    try {
      await firebaseAuth.signOut();
      setMobileMenuOpen(false);
    } catch (error) {
      console.error("Sign Out Error", error);
    }
  };

  useEffect(() => {
    setFeedDrawerVisible(false);
    setMobileMenuOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (!mobileMenuOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setMobileMenuOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [mobileMenuOpen]);

  const userMenuItems = [
    {
      label: <StyledLink to="/account">Account Settings</StyledLink>,
      key: "0",
    },
    {
      label: <StyledLink to="/presto">Dashboard</StyledLink>,
      key: "1",
    },
    {
      label: <StyledLink onClick={onLogout}>Log Out</StyledLink>,
      key: "2",
    },
  ];

  return (
    showHeaderUi(location) && (
      <Container $dark={isHomePage}>
        <LogoBarContainer>
          <Header $dark={isHomePage}>
            <HeaderInner>
              <SolvTaxLogo color={isHomePage ? "white" : "black"} />

              <NavLinks>
                {NAV_ITEMS.map((item) =>
                  item.hash ? (
                    <NavItem
                      key={item.to}
                      $dark={isHomePage}
                      to={item.to}
                      onClick={handleHowItWorksClick}
                    >
                      {item.label}
                    </NavItem>
                  ) : (
                    <NavItem key={item.to} $dark={isHomePage} to={item.to}>
                      {item.label}
                    </NavItem>
                  )
                )}
              </NavLinks>

              <HeaderActions>
                <AuthSection>
                  {!user ? (
                    <AuthButtons>
                      <LoginButton
                        type="button"
                        $dark={isHomePage}
                        variant="ghost"
                        size="sm"
                        width="auto"
                        rounded="slight"
                        onClick={handleOpenLogin}
                      >
                        Log In
                      </LoginButton>
                      <SignUpButton
                        type="button"
                        $dark={isHomePage}
                        variant="primary"
                        size="sm"
                        width="auto"
                        rounded="slight"
                        onClick={handleOpenWaitlist}
                      >
                        Request Access
                      </SignUpButton>
                    </AuthButtons>
                  ) : (
                    <UserSection>
                      <Hidden mobile>
                        <Link to="/presto">
                          <Button variant="primary" size="sm">
                            Dashboard
                          </Button>
                        </Link>
                      </Hidden>
                      <Dropdown
                        menu={{
                          items: userMenuItems,
                        }}
                        trigger={["click"]}
                        dropdownRender={(menu) => {
                          return <StyledDropdownMenu>{menu}</StyledDropdownMenu>;
                        }}
                      >
                        <ProfileButton onClick={(e) => e.preventDefault()}>
                          <ProfilePic src={userInfo?.pfp ?? profileIcon} alt="Profile" />
                        </ProfileButton>
                      </Dropdown>
                    </UserSection>
                  )}
                </AuthSection>

                <MenuToggle
                  type="button"
                  $dark={isHomePage}
                  aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
                  aria-expanded={mobileMenuOpen}
                  onClick={() => setMobileMenuOpen((open) => !open)}
                >
                  {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
                </MenuToggle>
              </HeaderActions>
            </HeaderInner>
          </Header>

          <MobileMenu $open={mobileMenuOpen} $dark={isHomePage}>
            {NAV_ITEMS.map((item) =>
              item.hash ? (
                <MobileNavItem
                  key={item.to}
                  $dark={isHomePage}
                  to={item.to}
                  onClick={handleHowItWorksClick}
                >
                  {item.label}
                </MobileNavItem>
              ) : (
                <MobileNavItem
                  key={item.to}
                  $dark={isHomePage}
                  to={item.to}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.label}
                </MobileNavItem>
              )
            )}
            {!user ? (
              <MobileAuthRow $dark={isHomePage}>
                <MobileAuthButton type="button" $dark={isHomePage} onClick={handleOpenLogin}>
                  Log In
                </MobileAuthButton>
                <MobileAuthButton
                  type="button"
                  $dark={isHomePage}
                  $primary
                  onClick={handleOpenWaitlist}
                >
                  Request Access
                </MobileAuthButton>
              </MobileAuthRow>
            ) : (
              <MobileAuthRow $dark={isHomePage}>
                <MobileNavItem
                  $dark={isHomePage}
                  to="/account"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Account Settings
                </MobileNavItem>
                <MobileNavItem
                  $dark={isHomePage}
                  to="/presto"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Dashboard
                </MobileNavItem>
                <MobileAuthButton type="button" $dark={isHomePage} onClick={onLogout}>
                  Log Out
                </MobileAuthButton>
              </MobileAuthRow>
            )}
          </MobileMenu>
        </LogoBarContainer>

        <FeedDrawer isOpen={feedDrawerVisible} onClose={() => setFeedDrawerVisible(false)} />

        <EditModal isOpen={isEditParamSet && !isMobile} onClose={() => removeEditParam()} />

        <SettingsModal
          isOpen={isSettingsParamSet && !isMobile}
          onClose={() => removeSettingsParam()}
        />
      </Container>
    )
  );
}

const Container = styled.div`
  position: sticky;
  top: 0;
  z-index: 40;
  width: 100%;
  background-color: ${(p) => (p.$dark ? "#0f172a" : "var(--color-gray-0)")};
  border-bottom: ${(p) => (p.$dark ? "1px solid rgba(15, 23, 42, 0.85)" : "none")};
  box-shadow: ${(p) => (p.$dark ? "none" : "0px 2px 4px rgba(0, 0, 0, 0.1)")};
`;

const LogoBarContainer = styled.div`
  z-index: 1;
  width: 100%;
  position: relative;
`;

const Header = styled.div`
  background-color: ${(p) => (p.$dark ? "#0f172a" : "white")};
  padding: 0 20px;
  height: 80px;
  display: flex;
  align-items: center;

  @media (${device.mobile}) {
    padding: 0 16px;
    height: 70px;
  }

  @media (min-width: 1024px) {
    padding: 0 40px;
  }

  @media (min-width: 1280px) {
    padding: 0 48px;
  }
`;

const HeaderInner = styled.div`
  width: 100%;
  max-width: 80rem;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const NavLinks = styled.div`
  display: none;
  align-items: center;
  gap: 24px;
  margin-left: auto;

  @media (min-width: 768px) {
    display: flex;
  }

  @media (min-width: 1024px) {
    gap: 32px;
  }
`;

const NavItem = styled(NavLink)`
  font-weight: 500;
  color: ${(p) => (p.$dark ? "#cbd5e1" : "var(--neutral-600, #525252)")};
  font-family: "Inter", sans-serif;
  font-size: 16px;
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover {
    color: ${(p) => (p.$dark ? "#f8fafc" : "var(--primary, #1a4a73)")};
  }

  &.active {
    color: ${(p) => (p.$dark ? "#f8fafc" : "var(--primary, #1a4a73)")};
    font-weight: 600;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 16px;

  @media (min-width: 768px) {
    gap: 16px;
    margin-left: 24px;
  }
`;

const AuthSection = styled.div`
  display: none;
  align-items: center;
  gap: 16px;

  @media (min-width: 768px) {
    display: flex;
  }
`;

const AuthButtons = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const LoginButton = styled(Button)`
  color: ${(p) => (p.$dark ? "#e2e8f0" : "#000000")} !important;
  min-width: 100px;

  &:hover {
    color: ${(p) => (p.$dark ? "#f8fafc" : "#000000")} !important;
    background: ${(p) => (p.$dark ? "rgba(30, 41, 59, 0.6)" : "transparent")} !important;
  }
`;

const SignUpButton = styled(Button)`
  background-color: ${(p) => (p.$dark ? "#22d3ee" : "#1a4a73")} !important;
  color: ${(p) => (p.$dark ? "#0f172a" : "white")} !important;
  border: none !important;
  border-radius: 6px !important;
  font-weight: 600 !important;
  letter-spacing: 0.04em;
  min-width: 100px;

  &:hover {
    background-color: ${(p) => (p.$dark ? "#67e8f9" : "#153a5c")} !important;
    color: ${(p) => (p.$dark ? "#0f172a" : "white")} !important;
  }
`;

const MenuToggle = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 8px;
  border: 1px solid ${(p) => (p.$dark ? "rgba(148, 163, 184, 0.35)" : "rgba(15, 23, 42, 0.12)")};
  background: ${(p) => (p.$dark ? "rgba(30, 41, 59, 0.55)" : "#ffffff")};
  color: ${(p) => (p.$dark ? "#f8fafc" : "#0f172a")};
  cursor: pointer;

  @media (min-width: 768px) {
    display: none;
  }
`;

const MobileMenu = styled.nav`
  display: ${(p) => (p.$open ? "flex" : "none")};
  flex-direction: column;
  gap: 4px;
  padding: 12px 16px 20px;
  background: ${(p) => (p.$dark ? "#0f172a" : "#ffffff")};
  border-top: 1px solid ${(p) => (p.$dark ? "rgba(148, 163, 184, 0.2)" : "rgba(15, 23, 42, 0.08)")};

  @media (min-width: 768px) {
    display: none;
  }
`;

const MobileNavItem = styled(NavLink)`
  display: block;
  padding: 12px 10px;
  border-radius: 8px;
  font-weight: 500;
  font-size: 15px;
  text-decoration: none;
  color: ${(p) => (p.$dark ? "#e2e8f0" : "#334155")};

  &:hover,
  &.active {
    background: ${(p) => (p.$dark ? "rgba(30, 41, 59, 0.8)" : "#f1f5f9")};
    color: ${(p) => (p.$dark ? "#f8fafc" : "#0f172a")};
  }
`;

const MobileAuthRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
  padding-top: 12px;
  border-top: 1px solid ${(p) => (p.$dark ? "rgba(148, 163, 184, 0.2)" : "rgba(15, 23, 42, 0.08)")};
`;

const MobileAuthButton = styled.button`
  width: 100%;
  padding: 12px 14px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  border: 1px solid
    ${(p) =>
      p.$primary
        ? "transparent"
        : p.$dark
        ? "rgba(148, 163, 184, 0.35)"
        : "rgba(15, 23, 42, 0.12)"};
  background: ${(p) =>
    p.$primary ? (p.$dark ? "#22d3ee" : "#1a4a73") : p.$dark ? "transparent" : "#ffffff"};
  color: ${(p) =>
    p.$primary ? (p.$dark ? "#0f172a" : "#ffffff") : p.$dark ? "#e2e8f0" : "#0f172a"};
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  button {
    white-space: nowrap;
    min-width: fit-content;
  }
`;

const ProfileButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  border-radius: 50%;
  overflow: hidden;
  width: 40px;
  height: 40px;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const ProfilePic = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
`;

const StyledDropdownMenu = styled.div`
  max-height: 400px;
  overflow: auto;
  border-radius: 8px;
  box-shadow: 0px 4px 12.4px 0px rgba(21, 21, 21, 0.74);

  .ant-dropdown-menu {
    border: 0 !important;
    background: white !important;

    a {
      color: black !important;
    }

    li:hover {
      background-color: var(--neutral-50, #f9fafb) !important;
    }

    .ant-dropdown-menu-item-divider {
      background-color: rgba(0, 0, 0, 0.1) !important;
    }
  }
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  width: 100%;
`;
